<template>
  <div class="event-card">
    <img :src="eventImage" :alt="eventName" class="event-card__image" />

    <div class="event-card__content">
      <p class="event-card__content-date">{{ startDate }} – {{ endDate }}</p>
      <h2 class="event-card__content-title">{{ eventName }}</h2>
      <p class="event-card__content-text">{{ eventDescription }}</p>

      <PrimaryButton class="event-card__content-btn" @click="$emit('click')">
        <template v-if="event.active">
          {{ $t("common.eventCardGo") }}
        </template>

        <template v-else>
          {{ $t("common.eventCardLook") }}
        </template>
      </PrimaryButton>
    </div>
    <div class="event-calendar">
      <h3 class="event-calendar__title">
        {{ $t("preparationCalendar.title") }}
      </h3>

      <ul class="event-calendar__list">
        <li class="event-calendar__list-item">
          <span
            class="event-calendar__list-item_marker"
            :class="{
              passed: !hasCompleteCompany,
              active: hasCompleteCompany
            }"
          >
            <icon
              v-if="hasCompleteCompany"
              name="done"
              class="event-calendar__list-icon"
            ></icon>
            <icon
              v-else
              name="warning--no-bg"
              class="event-calendar__list-icon"
            ></icon>
          </span>
          <span> {{ $t("preparationCalendar.company") }}</span>
        </li>

        <li class="event-calendar__list-item" v-if="isShowCatalog">
          <span
            class="event-calendar__list-item_marker"
            :class="{
              passed: !hasOrder,
              active: hasOrder
            }"
          >
            <icon
              v-if="hasOrder"
              name="done"
              class="event-calendar__list-icon"
            ></icon>
            <icon
              v-else
              name="warning--no-bg"
              class="event-calendar__list-icon"
            ></icon>
          </span>
          <span>
            {{
              $t("preparationCalendar.order", {
                date: catalogApplicationDeadline.format("DD MMMM YYYY")
              })
            }}
          </span>
        </li>

        <li class="event-calendar__list-item" v-if="isShowPasses">
          <span
            class="event-calendar__list-item_marker"
            :class="{
              passed: !event.quota?.certificate_count,
              active: event.quota?.certificate_count
            }"
          >
            <icon
              v-if="event.quota?.certificate_count"
              name="done"
              class="event-calendar__list-icon"
            ></icon>
            <icon
              v-else
              name="warning--no-bg"
              class="event-calendar__list-icon"
            ></icon>
          </span>
          <span>
            {{
              $t("preparationCalendar.pass", {
                date: passesApplicationDeadline.format("DD MMMM YYYY")
              })
            }}
          </span>
        </li>
        <li v-if="isShowAddServices" class="event-calendar__list-item">
          <span
            class="event-calendar__list-item_marker"
            :class="{
              passed: !ordersCount,
              active: ordersCount
            }"
          >
            <icon
              v-if="ordersCount"
              name="done"
              class="event-calendar__list-icon"
            ></icon>
            <icon
              v-else
              name="warning--no-bg"
              class="event-calendar__list-icon"
            ></icon>
          </span>
          <span>
            {{
              $t("preparationCalendar.addServices", {
                date: additionalOrderDeadline.format("DD MMMM YYYY")
              })
            }}
          </span>
        </li>
      </ul>
    </div>
  </div>
</template>

<script>
import { i18n } from "@v2/shared/config";
import image from "/public/static/images/event-no-company-image.svg";
import { PrimaryButton } from "@v2/shared/ui/components";
import hasCompleteCompany from "@/helpers/event";
import moment from "moment/moment";

export default {
  name: "EventCard",

  components: { PrimaryButton },

  data() {
    return {
      noPhoto: image
    };
  },

  props: {
    event: {
      type: Object,
      required: true
    },

    isAddCard: {
      type: Boolean,
      default: false
    }
  },

  computed: {
    isShowCatalog() {
      return (
        this.event.catalog_template_id &&
        this.event.type !== "special-project" &&
        this.event.type !== "organizer-and-media" &&
        this.event.type !== "installer"
      );
    },
    isShowPasses() {
      return this.event.type !== "part-time";
    },
    isShowAddServices() {
      return (
        this.event.is_show_order_additional_services &&
        this.event.type !== "special-project" &&
        this.event.type !== "organizer-and-media" &&
        this.event.type !== "part-time" &&
        this.event.type !== "installer"
      );
    },
    startDate() {
      return moment(this.exhibition.started_at)
        .locale(i18n.locale)
        .format("DD MMMM YYYY");
    },
    endDate() {
      return moment(this.exhibition.ended_at)
        .locale(i18n.locale)
        .format("DD MMMM YYYY");
    },

    catalogApplicationDeadline() {
      return moment(this.event.exhibition.catalog_order_ended_at).locale(
        i18n.locale
      );
    },
    passesApplicationDeadline() {
      const passDeadline = {
        certificate: this.event.exhibition.personal_pass_ended_at,
        installation: this.event.exhibition.installer_pass_ended_at
      };

      if (this.event.type === "installer") {
        return moment(passDeadline.installation).locale(i18n.locale);
      }

      if (this.event.type !== "installer" && this.event.is_self_builder) {
        const closestDeadline =
          moment(this.event.exhibition.personal_pass_ended_at) >
          moment(this.event.exhibition.installer_pass_ended_at)
            ? this.event.exhibition.installer_pass_ended_at
            : this.event.exhibition.personal_pass_ended_at;

        return moment(closestDeadline).locale(i18n.locale);
      }
      return moment(passDeadline.certificate).locale(i18n.locale);
    },
    additionalOrderDeadline() {
      return moment(this.event.exhibition.additional_order_deadline);
    },
    ordersCount() {
      return this.event.additional_orders_count || 0;
    },
    hasCompleteCompany() {
      return hasCompleteCompany(this.event.company);
    },

    hasOrder() {
      return (
        this.event.catalog_order_id &&
        this.event.catalog_order_status !== "draft"
      );
    },

    exhibition() {
      return this.event.exhibition || {};
    },

    eventImage() {
      return this.exhibition.image?.thumbnail_path
        ? `${process.env.VUE_APP_URL}storage/${this.exhibition.image.thumbnail_path}`
        : this.noPhoto;
    },

    eventName() {
      return i18n.locale === "ru"
        ? this.exhibition.name_rus
        : this.exhibition.name_eng;
    },

    eventDescription() {
      return i18n.locale === "ru"
        ? this.exhibition.description_rus
        : this.exhibition.description_eng;
    }
  }
};
</script>

<style lang="scss" scoped>
@import "@v2/app/styles/mixins";
@import "@v2/app/styles/media";
.event {
  &-card {
    display: flex;

    border-radius: 2px;
    border: 1px solid var(--gray-darker);
    background-color: #ffffff;
    width: 100%;

    margin-bottom: 22px;
    position: relative;
    &__montage-info {
      width: 348px;
      flex-shrink: 0;

      border-left: 1px solid rgba(#d8d8d8, 0.5);

      box-sizing: border-box;

      padding: 38px 48px;

      &-title {
        @include h3();
        margin-bottom: 27px;
      }
    }

    &__image {
      width: 112px;
      padding: 30px;
      flex-shrink: 0;
      align-self: flex-start;

      @include respond-down(lg) {
        width: 80px;
        margin-top: 32px;
      }

      @include respond-down(md) {
        width: 48px;
        padding: 15px;
      }
    }

    &__content {
      display: flex;
      flex-direction: column;
      align-items: flex-start;

      min-width: 387px;
      max-width: 711px;

      padding: 30px 30px 30px 0;

      @include respond-down(lg) {
        min-width: 290px;
        margin-right: 0;
        box-sizing: border-box;
        padding-right: 30px;
      }

      @include respond-down(md) {
        padding: 15px 15px 15px 0;
        min-width: 184px;
      }

      &-date {
        font-size: 13px;

        line-height: 1.54;

        color: #000000;

        margin: 0;

        // for ie11
        width: 100%;

        @include respond-down(md) {
          font-size: 12px;
        }
      }

      &-title {
        font-size: 21px;
        font-weight: 500;

        line-height: 1.14;

        color: #262626;

        margin: 14px 0 0 0;

        // for ie11
        width: 100%;

        @include respond-down(md) {
          font-size: 16px;
          margin-top: 8px;
          word-break: break-all;
        }
      }

      &-text {
        color: #6d7179;
        font-size: 13px;
        line-height: 1.54;
        margin: 16px 0 30px 0;

        // for ie11
        width: 100%;

        @include respond-down(md) {
          font-size: 12px;
          margin-bottom: 24px;
        }
      }

      &-btn {
        margin-top: auto;
      }
    }
  }

  &-calendar {
    @include respond-down(md) {
      display: none;
    }

    border-left: 1px solid var(--gray-darker);
    text-align: left;

    padding: 30px;

    width: 351px;
    flex-shrink: 0;
    box-sizing: border-box;

    @include respond-down(lg) {
      width: 300px;
      padding: 30px;
    }

    @include respond-down(md) {
      padding: 15px;
    }

    &__title {
      font-size: 16px;
      font-weight: 500;
      line-height: 1.5;
      color: #505050;
      margin: 0 0 24px 0;
    }

    &__list {
      list-style: none;
      padding: 0;
      &-icon {
        width: 16px;
        height: 16px;
      }
      &-item {
        display: flex;
        align-items: center;
        color: #000;
        font-size: 13px;
        font-weight: normal;
        font-style: normal;
        font-stretch: normal;
        line-height: 1.43;
        letter-spacing: normal;
        &_marker {
          width: 32px;
          height: 32px;
          border-radius: 20px;

          flex-shrink: 0;

          margin-right: 16.2px;

          display: flex;
          align-items: center;
          justify-content: center;
          box-sizing: border-box;
          position: relative;
          &.active {
            background-color: #005197;
            border: 1px solid #005197;
          }
          &.passed {
            border: 1px solid #a3a8b3;
            background-color: transparent;
          }
        }

        &:not(:last-child) {
          margin-bottom: 19px;
        }
      }
    }
  }
}
</style>
